import { ChangeDetectorRef, Component, ViewContainerRef, inject } from '@angular/core';
import $ from 'jquery';
import { GlobalState } from './global.state';
import { ToastrService } from "ngx-toastr";
import { Router, NavigationStart, NavigationEnd, RouterModule } from "@angular/router";
import { UtilityHelper } from "./helpers/utilityHelper";
import { SessionCleanupService } from "./helpers/session-cleanup.service";
import { AbstractBaseComponent } from "./app.baseComponent";
import { MentoringEvents } from "./helpers/events";
import { environment } from "../environments/environment";
import { AuthenticationHelper } from "./helpers/authenticationHelper";
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { MentoringServices } from './appServices/mentoring';
import { CurrentUserStateService } from './appServices/current-user-state.service';
import { PopUpScreenComponent } from './theme/components/popUpScreen/popUpScreen.component';
import { AppConstant } from './app.constant';
declare var gtag: Function;
declare let window: any;
import { CommonModule } from '@angular/common';
import { RouterOutlet } from '@angular/router';  // Import RouterOutlet
import { NgxPaginationModule } from 'ngx-pagination';
import { filter, take } from 'rxjs/operators';
import { GlobalPopupContainerComponent } from './shared/global-popup-container/global-popup-container.component';

@Component({
    selector: 'app',
    styleUrls: ['./app.component.scss'],
    template: `
    <main [ngClass]="{ 'menu-collapsed': isMenuCollapsed }">
      <div class="additional-bg"></div>
      <div id="preloaderContent" style="display:none">
        <div class="ring-spinner"></div>
      </div>
      <app-pop-up-screen></app-pop-up-screen>
      <router-outlet></router-outlet>
      <global-popup-container></global-popup-container>
    </main>
  `,
    imports: [PopUpScreenComponent, CommonModule, RouterOutlet, NgxPaginationModule, RouterModule, GlobalPopupContainerComponent]
})
export class App extends AbstractBaseComponent {
  isMenuCollapsed: boolean = false;
  /** Eagerly construct so logout sessionCleared listeners are always registered. */
  private readonly sessionCleanup = inject(SessionCleanupService);

  constructor(
    private _state: GlobalState,
    private viewContainerRef: ViewContainerRef,
    private toastr: ToastrService,
    public router: Router,
    private utility: UtilityHelper,
    private authService: AuthenticationHelper,
    private mentoringEvents: MentoringEvents,
    private translate: TranslateService,
    private mentoringServices: MentoringServices,
    private currentUserState: CurrentUserStateService,
    private cdr: ChangeDetectorRef
  ) {
    super();
    this.applyGA();
    this.setAppFavicon();
    this._state.subscribe('menu.isCollapsed', (isCollapsed) => {
      this.isMenuCollapsed = isCollapsed;
    });

    this.mentoringEvents.getSessionCleared().subscribe(() => {
      this.currentUserState.clearAll();
      this.authService.clearActivityTracking();
    });

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        const targetUrl = event.url;
        const isMentorHubRoute = targetUrl === '/mentor-hub' || targetUrl.startsWith('/mentor-hub/');
        if (isMentorHubRoute) {
          this.utility.setItem('transitionedFromCard', 'false');
        }
      }
      if (event instanceof NavigationEnd && this.utility.myDomain !== this.LOCAL_DOMAIN) {
        this.sendDataToGA(event.urlAfterRedirects);
      }
    });
  }

  ngOnInit() {
    // ✅ Reset video call status on page refresh (WebRTC connections don't persist across refreshes)
    this.utility.setItem('isVideCallLive', false);

    if (!this.authService.isLoggedIn()) {
      AppConstant.clearSignOutInProgress();
    }
    
    // Check if role is already available and start monitoring immediately
    const role = this.utility.getItem('role');
    if (role === 'coordinator') {
      this.authService.forceStartMonitoring();
    } else {
      this.waitForRoleAndStartMonitoring();
    }
    const userId = this.utility.getItem('userId');
    if (!this.utility.getItem('currentLanguage')) {
      this.utility.setItem('currentLanguage', 'en');
    } else if (this.authService.isLoggedIn()) {
      const params: any = [`user_details=ethnicity`,].join(`&`);
      const ethincityURL: any = `${userId}?${params}`;
      this.mentoringServices.getMyAccountDetails(ethincityURL).subscribe(
        (data) => {
          if (data && data.data && data.data.attributes && data.data.attributes.language == 'dy') {
            this.utility.setItem('currentLanguage', 'wls');
            this.translate.use('wls');
          } else {
            this.utility.setItem('currentLanguage', 'en');
            this.translate.use('en');
          }
        },
        (error) => {
          console.warn('Failed to fetch user language preference, defaulting to English.', error);
          this.utility.setItem('currentLanguage', 'en');
          this.translate.use('en');
        }
      );
    }
    this.translate.addLangs(['en', 'wls']);
    let currentCountry = this.utility.getItem('currentLanguage');
    this.translate.use(currentCountry);
    this.translate.setDefaultLang('en');
    if (this.utility.getItem('currentLanguage') === 'wls') {
      moment.locale('cy');
    }

    this.mentoringEvents.getProjectSwitchedContent().subscribe(item => {
      if (item) {
        if (this.router.url.includes('feeds') && !item.reload) {
          this.sendDataToGA(this.router.url);
        }
      }
    });
  }
  waitForRoleAndStartMonitoring() {
    const checkInterval = setInterval(() => {
      const role = this.utility.getItem('role');
      if (role) {
        clearInterval(checkInterval);
        if (role == 'coordinator') {
          this.authService.forceStartMonitoring();
        }
      }
    }, 200);
  }

  ngOnDestroy() {
    // Cleanup authentication monitoring when component is destroyed
    this.authService.stopMonitoring();
  }

  sendDataToGA(urlAfterRedirects) {
    gtag('set', 'page', urlAfterRedirects);
    if (this.utility.getItem('role') == 'mentee' || this.utility.getItem('role') == 'mentor'
      || this.utility.getItem('role') == 'coordinator') {
      gtag('set', { 'user_properties': { 'role': this.utility.getItem('role') } });
    } else {
      gtag('set', { 'user_properties': { 'role': 'guest' } });
    }
    if (this.utility.getItem('partnerName')) {
      gtag('set', { 'user_properties': { 'partner': this.utility.getItem('partnerName') } });
    }
    if (this.utility.getItem('projectName')) {
      gtag('set', { 'user_properties': { 'project_name': this.utility.getItem('projectName') } });
    }
    gtag('event', 'page_view', {
      page_path: window.location.pathname,
      page_location: window.location.href,
      page_title: document.title
    });
  }

  ngAfterViewChecked(): void {
    this.cdr.detectChanges();
  }
  ngAfterViewInit() {
  this.router.events
        .pipe(
          filter(event => event instanceof NavigationEnd),
          take(1)
        )
        .subscribe(() => {
          const splash = document.getElementById('preloader');
          splash?.remove();
        });
    this._state.subscribe('menu.isCollapsed', (isCollapsed) => {
      this.isMenuCollapsed = isCollapsed;
    });

    this.mentoringEvents.getAnalyticsEvent().subscribe((item) => {
      if (item) {
        this.applyGA();
        gtag('event', 'page_events', item);
      }
    });
  }

  applyGA() {
    if (this.utility.myDomain !== this.LOCAL_DOMAIN) {
      const script = document.createElement('script');
      script.innerHTML = '';
      const appId = environment.appId;

      // Intercom setup
      script.innerHTML = `(function(){var w=window;var ic=w.Intercom;if(typeof ic==="function"){ic('reattach_activator');` +
        `ic('update',w.intercomSettings);}else{var d=document;var i=function(){i.c(arguments);};i.q=[];` +
        `i.c=function(args){i.q.push(args);};w.Intercom=i;var l=function(){var s=d.createElement('script');` +
        `s.type='text/javascript';s.async=true;s.src='https://widget.intercom.io/widget/${appId}';` +
        `var x=d.getElementsByTagName('script')[0];x.parentNode.insertBefore(s, x);};` +
        `if(document.readyState==='complete'){l();}else if(w.attachEvent){w.attachEvent('onload',l);` +
        `}else{w.addEventListener('load',l,false);}}})();`;

      // Google Analytics gtag setup
      script.innerHTML += `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':` +
        `new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],` +
        `j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=` +
        `'https://www.googletagmanager.com/gtag/js?id=' + i;f.parentNode.insertBefore(j,f);` +
        `})(window,document,'script','dataLayer','${this.getGAId()}');`;

      script.innerHTML += `window.dataLayer = window.dataLayer || [];` +
        `function gtag(){dataLayer.push(arguments);}` +
        `gtag('js', new Date());` +
        `gtag('config', '${this.getGAId()}');`;

      const head = document.getElementsByTagName('head')[0];
      head.appendChild(script);
    }
  }

  getGAId() {
    return this.utility.myDomain === this.KNOWLEDGE_DOMAIN
      ? environment.UAIDForKnowledge
      : environment.UAIDForMentoring;
  }

  setAppFavicon() {
    let iconPath: any = './assets/img/mentoring.ico';
    if (this.utility.myDomain === this.KNOWLEDGE_DOMAIN) {
      iconPath = './assets/img/knowledge.ico';
      $('#navigation-tab-title').text('Bright Knowledge');
      $('#site-favicon').attr('href', iconPath);
    }
  }

  get currentUserId() {
    return this.utility.currentUserID;
  }

  set currentUserId(value) {
    this.utility.currentUserID = value;
  }

  get userIdFromUtility() {
    return this.utility.getItem('userId') ? this.utility.getItem('userId') : '';
  }
}
